import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  Link as LinkIcon,
  Image,
  Globe,
  Code,
  MapPin,
  ExternalLink,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Target,
  BarChart3,
  Zap,
  Settings,
  BookOpen,
  Layers
} from "lucide-react";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const SEODashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/seo/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error("Error fetching SEO dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500/10 border-green-500/30";
    if (score >= 60) return "bg-yellow-500/10 border-yellow-500/30";
    if (score >= 40) return "bg-orange-500/10 border-orange-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  const seoTools = [
    {
      id: "keyword-research",
      title: "Keyword Research",
      description: "AI-powered keyword discovery and analysis",
      icon: Search,
      link: "/admin/seo/keywords",
      color: "text-blue-400"
    },
    {
      id: "content-optimizer",
      title: "Content Optimizer",
      description: "Optimize your pages for better rankings",
      icon: FileText,
      link: "/admin/seo/optimizer",
      color: "text-green-400"
    },
    {
      id: "meta-generator",
      title: "Meta Tag Generator",
      description: "Generate optimized titles & descriptions",
      icon: Code,
      link: "/admin/seo/meta",
      color: "text-purple-400"
    },
    {
      id: "schema-generator",
      title: "Schema Generator",
      description: "Create structured data markup",
      icon: Layers,
      link: "/admin/seo/schema",
      color: "text-cyan-400"
    },
    {
      id: "internal-linking",
      title: "Internal Linking",
      description: "Discover linking opportunities",
      icon: LinkIcon,
      link: "/admin/seo/linking",
      color: "text-orange-400"
    },
    {
      id: "local-seo",
      title: "Local SEO",
      description: "Optimize for local search",
      icon: MapPin,
      link: "/admin/seo/local",
      color: "text-red-400"
    },
    {
      id: "image-seo",
      title: "Image SEO",
      description: "Optimize images for search",
      icon: Image,
      link: "/admin/seo/images",
      color: "text-pink-400"
    },
    {
      id: "off-page",
      title: "Off-Page Strategy",
      description: "Link building & outreach planning",
      icon: ExternalLink,
      link: "/admin/seo/off-page",
      color: "text-indigo-400"
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
            <p className="text-[#A1A1AA]">Loading SEO Dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[#D4AF37]" />
              AI SEO Center
            </h1>
            <p className="text-[#A1A1AA] mt-2">
              Comprehensive SEO management powered by AI
            </p>
          </div>
          <button
            onClick={fetchDashboard}
            className="flex items-center gap-2 px-4 py-2 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Health Score Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-8 border rounded-lg ${getScoreBg(dashboard?.health_score || 0)}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#A1A1AA] text-sm uppercase tracking-wider">Site Health Score</h3>
              <BarChart3 className={`w-5 h-5 ${getScoreColor(dashboard?.health_score || 0)}`} />
            </div>
            <div className={`text-6xl font-bold ${getScoreColor(dashboard?.health_score || 0)}`}>
              {dashboard?.health_score || 0}
            </div>
            <p className="text-[#A1A1AA] mt-2">out of 100</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-8 border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A]"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#A1A1AA] text-sm uppercase tracking-wider">Total Pages</h3>
              <FileText className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="text-5xl font-bold text-[#F5F5F0]">
              {dashboard?.total_pages || 0}
            </div>
            <p className="text-[#A1A1AA] mt-2">
              {dashboard?.pages_with_seo_settings || 0} with SEO settings
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-8 border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A]"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#A1A1AA] text-sm uppercase tracking-wider">Issues Found</h3>
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-5xl font-bold text-orange-500">
              {dashboard?.total_issues || 0}
            </div>
            <p className="text-[#A1A1AA] mt-2">
              across all pages
            </p>
          </motion.div>
        </div>

        {/* SEO Tools Grid */}
        <div>
          <h2 className="text-xl font-semibold text-[#F5F5F0] mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D4AF37]" />
            AI SEO Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {seoTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={tool.link}
                  className="block p-6 border border-[rgba(212,175,55,0.15)] rounded-lg bg-[#0A0A0A] hover:border-[rgba(212,175,55,0.4)] hover:bg-[#0F0F0F] transition-all group"
                >
                  <tool.icon className={`w-8 h-8 ${tool.color} mb-4 group-hover:scale-110 transition-transform`} />
                  <h3 className="text-[#F5F5F0] font-semibold mb-2">{tool.title}</h3>
                  <p className="text-[#A1A1AA] text-sm">{tool.description}</p>
                  <div className="flex items-center gap-1 text-[#D4AF37] text-sm mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open tool <ChevronRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Issues Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Issues */}
          <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
            <h3 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Issues to Fix
            </h3>
            <div className="space-y-3">
              {dashboard?.issue_counts?.missing_meta_description > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="text-[#F5F5F0]">Missing Meta Descriptions</span>
                  </div>
                  <span className="text-red-500 font-semibold">{dashboard.issue_counts.missing_meta_description}</span>
                </div>
              )}
              {dashboard?.issue_counts?.missing_focus_keyword > 0 && (
                <div className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/20 rounded">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-orange-500" />
                    <span className="text-[#F5F5F0]">Missing Focus Keywords</span>
                  </div>
                  <span className="text-orange-500 font-semibold">{dashboard.issue_counts.missing_focus_keyword}</span>
                </div>
              )}
              {dashboard?.issue_counts?.missing_schema > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
                  <div className="flex items-center gap-3">
                    <Code className="w-5 h-5 text-yellow-500" />
                    <span className="text-[#F5F5F0]">Missing Schema Markup</span>
                  </div>
                  <span className="text-yellow-500 font-semibold">{dashboard.issue_counts.missing_schema}</span>
                </div>
              )}
              {dashboard?.issue_counts?.missing_og_image > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/20 rounded">
                  <div className="flex items-center gap-3">
                    <Image className="w-5 h-5 text-blue-500" />
                    <span className="text-[#F5F5F0]">Missing OG Images</span>
                  </div>
                  <span className="text-blue-500 font-semibold">{dashboard.issue_counts.missing_og_image}</span>
                </div>
              )}
              {dashboard?.total_issues === 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-500">No critical issues found!</span>
                </div>
              )}
            </div>
          </div>

          {/* Pages Needing Attention */}
          <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
            <h3 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Pages Needing SEO
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {dashboard?.issues?.missing_meta_description?.slice(0, 8).map((page, index) => (
                <Link
                  key={index}
                  to={`/admin/seo/page/${page.slug}`}
                  className="flex items-center justify-between p-3 border border-[rgba(212,175,55,0.1)] rounded hover:border-[rgba(212,175,55,0.3)] hover:bg-[#121212] transition-colors"
                >
                  <div>
                    <p className="text-[#F5F5F0] font-medium">{page.title}</p>
                    <p className="text-[#a1a1aa] text-sm">/{page.slug}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                </Link>
              ))}
              {(!dashboard?.issues?.missing_meta_description || dashboard.issues.missing_meta_description.length === 0) && (
                <p className="text-[#A1A1AA] text-center py-4">All pages have basic SEO setup</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
          <h3 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/seo/keywords"
              className="flex items-center gap-4 p-4 border border-[rgba(212,175,55,0.2)] rounded-lg hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                <Search className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[#F5F5F0] font-medium">Research Keywords</p>
                <p className="text-[#A1A1AA] text-sm">Find ranking opportunities</p>
              </div>
            </Link>
            <Link
              to="/admin/seo/optimizer"
              className="flex items-center gap-4 p-4 border border-[rgba(212,175,55,0.2)] rounded-lg hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-[#F5F5F0] font-medium">Optimize a Page</p>
                <p className="text-[#A1A1AA] text-sm">Improve your rankings</p>
              </div>
            </Link>
            <Link
              to="/admin/seo/meta"
              className="flex items-center gap-4 p-4 border border-[rgba(212,175,55,0.2)] rounded-lg hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                <Code className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-[#F5F5F0] font-medium">Generate Meta Tags</p>
                <p className="text-[#A1A1AA] text-sm">AI-powered titles & descriptions</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SEODashboard;
